import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    IO_KIND,
    TraceData,
    TraceDataRef,
    TraceIo,
    TraceIoKind,
} from '../../api/trace';
import { TraceChip } from './TraceChip';
import { TraceDataRefs } from './TraceDataRefs';

/** What sort of thing this is, before it is read. */
const IO_ICON: Record<TraceIoKind, IconProp> = {
    QueueMessage: 'envelope',
    Body: 'file-code',
    Row: 'table',
    HttpCall: 'right-to-bracket',
    ApiCall: 'paper-plane',
};

/** Which way the thing on this row is travelling. */
export type TraceIoDirection = 'in' | 'out';

const FLAG: Record<
    TraceIoDirection,
    { label: string; hint: string; icon: IconProp }
> = {
    in: {
        label: 'gaat erin',
        hint: 'Wat deze stap binnenkrijgt. Meer krijgt hij niet: alles wat hij verder nodig heeft, zoekt hij hier zelf bij.',
        icon: 'arrow-right',
    },
    out: {
        label: 'komt eruit',
        hint: 'Wat deze stap achterlaat en doorgeeft. Een queuebericht hieronder is precies het bericht dat de volgende stap oppakt.',
        icon: 'arrow-down',
    },
};

export interface ITraceValueProps {
    value: string;

    /** What it is, for the label of the copy button. */
    label?: string;

    hint?: string;
}

/**
 * One literal value, with a button to copy it.
 *
 * The values on this panel are the things you paste somewhere else: a queue message into a
 * storage browser, an ObjectGuid into a table filter, an Exact-id into Exact. Selecting them
 * by hand out of a wrapped line is how a guid loses its last character.
 */
export const TraceValue = (props: ITraceValueProps) => {
    const [copied, setCopied] = useState<boolean>(false);

    const copy = () => {
        void navigator.clipboard
            .writeText(props.value)
            .then(() => setCopied(true))
            .catch(() => undefined);
    };

    return (
        <span className="trace-value">
            <code className="trace-value__text" title={props.hint}>
                {props.value}
            </code>

            <button
                type="button"
                className="trace-value__copy"
                onClick={copy}
                title={`${props.label ?? 'Deze waarde'} kopiëren`}
                aria-label={`${props.label ?? 'Deze waarde'} kopiëren`}
            >
                <FontAwesomeIcon icon={copied ? 'check' : 'copy'} />
            </button>
        </span>
    );
};

export interface ITraceIoBlockProps {
    direction: TraceIoDirection;

    items: TraceIo[];

    /** The step these belong to, so two blocks cannot share an open state. */
    owner: string;

    /** Fetches a stored body, when a row offers one and somebody opens it. */
    read: (reference: TraceDataRef) => Promise<TraceData>;
}

/**
 * What goes into a step, above it, and what comes out of it, under it.
 *
 * The thing this panel was missing. A step said which state the object was in and which rows
 * were written there, and left out the one thing every question is actually about: the
 * message. Every worker in this connector is handed exactly one short comma-separated string
 * and looks the rest up itself, so "wat ging er nou de pipeline in" was a question you could
 * only answer by opening a failed attempt and reading its queue message - if there was one.
 *
 * Above and below rather than in a fold, and in that order on purpose: the output of one step
 * and the input of the next are the same message, so the two sit against each other across
 * the step boundary and a reader can follow one string straight down the page.
 */
export const TraceIoBlock = (props: ITraceIoBlockProps) => {
    const [open, setOpen] = useState<Set<number>>(new Set());

    const toggle = (index: number) =>
        setOpen((current) => {
            const next = new Set(current);

            if (!next.delete(index)) {
                next.add(index);
            }

            return next;
        });

    if (props.items.length === 0) {
        return null;
    }

    const flag = FLAG[props.direction];

    return (
        <div className={`trace-io trace-io--${props.direction}`}>
            <span className="trace-io__flag" title={flag.hint}>
                <FontAwesomeIcon icon={flag.icon} />
                {flag.label}
            </span>

            <ul className="trace-io__list">
                {props.items.map((item, index) => (
                    <li
                        className={`trace-io__row trace-io__row--${item.kind.toLowerCase()}`}
                        key={`${item.kind}-${item.label}-${index}`}
                    >
                        <span className="trace-io__head">
                            <FontAwesomeIcon
                                icon={IO_ICON[item.kind]}
                                className="trace-io__icon"
                                title={IO_KIND[item.kind].hint}
                            />

                            <span className="trace-io__label">
                                {item.label}
                            </span>

                            {item.where && (
                                <span className="trace-io__where">
                                    {props.direction === 'in' ? '←' : '→'}{' '}
                                    {item.where}
                                </span>
                            )}

                            {/*
                                Read or rebuilt, said on the row itself. The chain prints most
                                messages from the keys it has now, which is the right shape and
                                no proof of what travelled - and that difference decides how
                                much weight a reader can put on the field they are staring at.
                            */}
                            {item.value &&
                                (item.observed ? (
                                    <TraceChip
                                        tone="ok"
                                        icon="circle-check"
                                        label="gelezen"
                                        hint="Deze waarde staat er echt: hij komt uit een rij of uit het bericht dat nu in de queue ligt."
                                    />
                                ) : (
                                    <TraceChip
                                        tone="warn"
                                        icon="circle-question"
                                        label="gereconstrueerd"
                                        hint="Samengesteld uit de sleutels die nu bekend zijn. De vorm is goed; of het bericht er destijds letterlijk zo uitzag, is hier niet te zien."
                                    />
                                ))}

                            {item.hint && (
                                <button
                                    type="button"
                                    className="trace-io__why"
                                    onClick={() => toggle(index)}
                                    aria-expanded={open.has(index)}
                                    title="Wat is dit, en wie leest het?"
                                >
                                    <FontAwesomeIcon icon="circle-info" />
                                </button>
                            )}
                        </span>

                        {item.value && (
                            <TraceValue
                                value={item.value}
                                label={item.label}
                                hint={item.hint ?? undefined}
                            />
                        )}

                        {open.has(index) && item.hint && (
                            <p className="trace-io__hint">{item.hint}</p>
                        )}

                        {item.data && (
                            <TraceDataRefs
                                owner={`${props.owner}|${props.direction}|${index}`}
                                refs={[item.data]}
                                read={props.read}
                            />
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};
